import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Search } from 'lucide-react';
import Modal from '../Modal';
import { adminProductsService } from '../../../services/adminProducts.service';

interface Course {
  id: string;
  title: string;
  hidden: boolean;
}

interface Bundle {
  id: string;
  title: string;
  hidden: boolean;
}

interface VisibilityManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  courses: Course[];
  bundles: Bundle[];
  onRefresh: () => void;
}

export default function VisibilityManagerModal({
  isOpen,
  onClose,
  courses,
  bundles,
  onRefresh
}: VisibilityManagerModalProps) {
  const [activeTab, setActiveTab] = useState<'courses' | 'bundles'>('courses');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'visible' | 'hidden'>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  const items = activeTab === 'courses' ? courses : bundles;

  const filteredItems = items.filter(item => {
    // Apply search filter
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply visibility filter
    const matchesFilter =
      filterMode === 'all' ? true :
      filterMode === 'visible' ? !item.hidden :
      item.hidden;

    return matchesSearch && matchesFilter;
  });

  const handleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedItems);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedItems(newSet);
  };

  const handleBulkVisibilityChange = async (hidden: boolean) => {
    if (selectedItems.size === 0) {
      alert('Please select at least one item');
      return;
    }

    const confirmMessage = `Are you sure you want to ${hidden ? 'hide' : 'show'} ${selectedItems.size} ${activeTab}?`;
    if (!confirm(confirmMessage)) return;

    setIsProcessing(true);
    try {
      const itemIds = Array.from(selectedItems);

      const response = activeTab === 'courses'
        ? await adminProductsService.bulkUpdateCourseVisibility(itemIds, hidden)
        : await adminProductsService.bulkUpdateBundleVisibility(itemIds, hidden);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update visibility');
      }

      setSelectedItems(new Set());
      onRefresh();

      const successCount = response.data?.updated_count || selectedItems.size;
      alert(`Successfully updated ${successCount} ${activeTab}`);
    } catch (error: any) {
      console.error('Error updating visibility:', error);
      alert(`Failed to update visibility: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuickToggle = async (id: string, currentHidden: boolean) => {
    setIsProcessing(true);
    try {
      const itemIds = [id];
      const newHidden = !currentHidden;

      const response = activeTab === 'courses'
        ? await adminProductsService.bulkUpdateCourseVisibility(itemIds, newHidden)
        : await adminProductsService.bulkUpdateBundleVisibility(itemIds, newHidden);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update visibility');
      }

      onRefresh();
    } catch (error: any) {
      console.error('Error updating visibility:', error);
      alert(`Failed to update visibility: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset selections when switching tabs
  useEffect(() => {
    setSelectedItems(new Set());
    setSearchQuery('');
  }, [activeTab]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Visibility Manager"
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex space-x-2 border-b border-white/10">
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'courses'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Courses ({courses.length})
          </button>
          <button
            onClick={() => setActiveTab('bundles')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'bundles'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Bundles ({bundles.length})
          </button>
        </div>

        {/* Action Bar */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white text-sm"
              />
            </div>
          </div>

          {/* Filter */}
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as any)}
            className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white text-sm"
          >
            <option value="all">All</option>
            <option value="visible">Visible Only</option>
            <option value="hidden">Hidden Only</option>
          </select>

          {/* Select All */}
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onChange={handleSelectAll}
              className="w-5 h-5 rounded"
            />
            <span className="text-sm text-gray-300">Select All</span>
          </label>

          {/* Bulk Actions */}
          {selectedItems.size > 0 && (
            <>
              <button
                onClick={() => handleBulkVisibilityChange(false)}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg transition-colors text-green-400 text-sm disabled:opacity-50"
              >
                <Eye className="w-4 h-4" />
                <span>Show Selected ({selectedItems.size})</span>
              </button>
              <button
                onClick={() => handleBulkVisibilityChange(true)}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600/20 hover:bg-gray-600/30 border border-gray-500/30 rounded-lg transition-colors text-gray-400 text-sm disabled:opacity-50"
              >
                <EyeOff className="w-4 h-4" />
                <span>Hide Selected ({selectedItems.size})</span>
              </button>
            </>
          )}
        </div>

        {/* Items Table */}
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-900/95 backdrop-blur">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Select</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Quick Toggle</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    No {activeTab} found
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => toggleSelection(item.id)}
                        className="w-5 h-5 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-white">{item.title}</td>
                    <td className="px-4 py-3">
                      {item.hidden ? (
                        <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-600/20 text-gray-400 border border-gray-600/30">
                          <EyeOff className="w-3 h-3" />
                          <span>Hidden</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                          <Eye className="w-3 h-3" />
                          <span>Visible</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleQuickToggle(item.id, item.hidden)}
                        disabled={isProcessing}
                        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-lg transition-colors text-sm disabled:opacity-50 ${
                          item.hidden
                            ? 'bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 text-green-400'
                            : 'bg-gray-600/20 hover:bg-gray-600/30 border border-gray-500/30 text-gray-400'
                        }`}
                      >
                        {item.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        <span>{item.hidden ? 'Show' : 'Hide'}</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t border-white/10">
          <p className="text-sm text-gray-400">
            {filteredItems.length} of {items.length} {activeTab} shown
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
